/**
 * AccountingExportTab - Export sales data for Xero/MYOB
 * Generate CSV exports and accounting summaries
 */
import { useState, useEffect } from 'react';
import { 
  FileSpreadsheet, Download, Calendar, DollarSign, 
  RefreshCw, FileText, Calculator, TrendingUp
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { api } from '@/utils/apiClient';

export default function AccountingExportTab() {
  const [summary, setSummary] = useState(null);
  const [vatReport, setVatReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  
  // Date range
  const [startDate, setStartDate] = useState(() => {
    const d = new Date();
    d.setMonth(d.getMonth() - 1);
    return d.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [period, setPeriod] = useState('month');

  useEffect(() => {
    fetchSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period]);

  const fetchSummary = async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/accounting/summary', { params: { period } });
      setSummary(res.data);
    } catch (err) {
      console.error('Failed to fetch summary:', err);
    }
    setLoading(false);
  };

  const fetchVatReport = async () => {
    try {
      const res = await api.get('/admin/accounting/vat-report', { 
        params: { start_date: startDate, end_date: endDate } 
      });
      setVatReport(res.data);
    } catch (err) {
      toast.error('Failed to fetch VAT report');
    }
  };

  const downloadExport = async (format) => {
    setExporting(true);
    try {
      const res = await api.get('/admin/accounting/export', {
        params: { start_date: startDate, end_date: endDate, format },
        responseType: format === 'summary' ? 'json' : 'blob'
      });
      
      if (format === 'summary') {
        // Download as JSON
        const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `accounting_summary_${startDate}_to_${endDate}.json`;
        a.click();
        URL.revokeObjectURL(url);
      } else {
        // Download CSV
        const url = URL.createObjectURL(res.data);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${format}_export_${startDate}_to_${endDate}.csv`;
        a.click();
        URL.revokeObjectURL(url);
      }
      toast.success(`${format.toUpperCase()} export downloaded`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Export failed');
    }
    setExporting(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="accounting-export-tab">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
            <FileSpreadsheet className="w-6 h-6 text-emerald-600 dark:text-emerald-400" />
          </div>
          <div>
            <h2 className="text-2xl font-heading text-foreground">Accounting Export</h2>
            <p className="text-muted-foreground text-sm">Export to Xero, MYOB, or CSV</p>
          </div>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={fetchSummary}
          className="rounded-full"
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Period Summary */}
      {summary && (
        <div className="bg-card rounded-2xl border border-border p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading text-lg text-foreground">Period Summary</h3>
            <div className="flex gap-2">
              {['day', 'week', 'month'].map(p => (
                <Button
                  key={p}
                  size="sm"
                  variant={period === p ? 'default' : 'outline'}
                  onClick={() => setPeriod(p)}
                  className="rounded-full capitalize"
                >
                  {p}
                </Button>
              ))}
            </div>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-muted/30 rounded-xl p-4">
              <div className="flex items-center gap-2 text-muted-foreground mb-1">
                <DollarSign className="w-4 h-4" />
                <span className="text-sm">Gross Sales</span>
              </div>
              <div className="text-2xl font-bold text-foreground">
                ${summary.summary?.gross_sales?.toFixed(2) || '0.00'}
              </div>
            </div>
            <div className="bg-muted/30 rounded-xl p-4">
              <div className="flex items-center gap-2 text-muted-foreground mb-1">
                <TrendingUp className="w-4 h-4" />
                <span className="text-sm">Net Sales</span>
              </div>
              <div className="text-2xl font-bold text-foreground">
                ${summary.summary?.net_sales?.toFixed(2) || '0.00'}
              </div>
            </div>
            <div className="bg-muted/30 rounded-xl p-4">
              <div className="flex items-center gap-2 text-muted-foreground mb-1">
                <Calculator className="w-4 h-4" />
                <span className="text-sm">VAT Collected</span>
              </div>
              <div className="text-2xl font-bold text-amber-600 dark:text-amber-400">
                ${summary.summary?.vat_collected?.toFixed(2) || '0.00'}
              </div>
            </div>
            <div className="bg-muted/30 rounded-xl p-4">
              <div className="flex items-center gap-2 text-muted-foreground mb-1">
                <FileText className="w-4 h-4" />
                <span className="text-sm">Orders</span>
              </div>
              <div className="text-2xl font-bold text-foreground">
                {summary.summary?.order_count || 0}
              </div>
            </div>
          </div>

          {/* Payment Breakdown */}
          <div className="grid md:grid-cols-2 gap-4">
            <div className="bg-green-50 dark:bg-green-950/30 rounded-xl p-4 border border-green-200 dark:border-green-800">
              <div className="text-sm text-green-700 dark:text-green-300 mb-1">Cash Payments</div>
              <div className="text-xl font-bold text-green-700 dark:text-green-300">
                ${summary.by_payment_method?.cash?.toFixed(2) || '0.00'}
              </div>
            </div>
            <div className="bg-blue-50 dark:bg-blue-950/30 rounded-xl p-4 border border-blue-200 dark:border-blue-800">
              <div className="text-sm text-blue-700 dark:text-blue-300 mb-1">Card/EFTPOS</div>
              <div className="text-xl font-bold text-blue-700 dark:text-blue-300">
                ${summary.by_payment_method?.card_eftpos?.toFixed(2) || '0.00'}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Export Section */}
      <div className="bg-card rounded-2xl border border-border p-6">
        <h3 className="font-heading text-lg text-foreground mb-4">Export Data</h3>
        
        {/* Date Range */}
        <div className="grid md:grid-cols-2 gap-4 mb-6">
          <div>
            <label className="block text-sm text-muted-foreground mb-2">Start Date</label>
            <Input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="bg-background"
            />
          </div>
          <div>
            <label className="block text-sm text-muted-foreground mb-2">End Date</label>
            <Input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="bg-background"
            />
          </div>
        </div>

        {/* Export Buttons */}
        <div className="grid md:grid-cols-3 gap-4">
          <div className="p-4 bg-muted/30 rounded-xl text-center">
            <div className="w-12 h-12 rounded-xl bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center mx-auto mb-3">
              <FileSpreadsheet className="w-6 h-6 text-blue-600 dark:text-blue-400" />
            </div>
            <div className="font-medium text-foreground mb-1">Xero Export</div>
            <div className="text-xs text-muted-foreground mb-3">Compatible with Xero import</div>
            <Button 
              onClick={() => downloadExport('xero')} 
              disabled={exporting}
              className="w-full"
              size="sm"
            >
              <Download className="w-4 h-4 mr-2" />
              Download CSV
            </Button>
          </div>
          
          <div className="p-4 bg-muted/30 rounded-xl text-center">
            <div className="w-12 h-12 rounded-xl bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center mx-auto mb-3">
              <FileSpreadsheet className="w-6 h-6 text-purple-600 dark:text-purple-400" />
            </div>
            <div className="font-medium text-foreground mb-1">MYOB Export</div>
            <div className="text-xs text-muted-foreground mb-3">Compatible with MYOB import</div>
            <Button 
              onClick={() => downloadExport('myob')} 
              disabled={exporting}
              className="w-full"
              size="sm"
            >
              <Download className="w-4 h-4 mr-2" />
              Download CSV
            </Button>
          </div>
          
          <div className="p-4 bg-muted/30 rounded-xl text-center">
            <div className="w-12 h-12 rounded-xl bg-green-100 dark:bg-green-900/30 flex items-center justify-center mx-auto mb-3">
              <FileText className="w-6 h-6 text-green-600 dark:text-green-400" />
            </div>
            <div className="font-medium text-foreground mb-1">Summary JSON</div>
            <div className="text-xs text-muted-foreground mb-3">Detailed period summary</div>
            <Button 
              onClick={() => downloadExport('summary')} 
              disabled={exporting}
              className="w-full"
              size="sm"
            >
              <Download className="w-4 h-4 mr-2" />
              Download JSON
            </Button>
          </div>
        </div>
      </div>

      {/* VAT Report */}
      <div className="bg-card rounded-2xl border border-border p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading text-lg text-foreground">VAT Report</h3>
          <Button size="sm" variant="outline" onClick={fetchVatReport} className="rounded-full">
            <Calculator className="w-4 h-4 mr-2" />
            Generate VAT Report
          </Button>
        </div>
        
        {vatReport ? (
          <div className="space-y-4">
            <div className="p-4 bg-amber-50 dark:bg-amber-950/30 rounded-xl border border-amber-200 dark:border-amber-800">
              <div className="text-sm text-amber-700 dark:text-amber-300 mb-2">
                Period: {vatReport.period?.start} to {vatReport.period?.end}
              </div>
              <div className="text-sm text-amber-700 dark:text-amber-300">
                VAT Rate: {vatReport.vat_rate}
              </div>
            </div>
            
            <div className="grid md:grid-cols-3 gap-4">
              <div className="bg-muted/30 rounded-xl p-4">
                <div className="text-sm text-muted-foreground">Gross (VAT Inclusive)</div>
                <div className="text-xl font-bold text-foreground">
                  ${vatReport.gross_sales_inclusive?.toFixed(2)}
                </div>
              </div>
              <div className="bg-muted/30 rounded-xl p-4">
                <div className="text-sm text-muted-foreground">Net (VAT Exclusive)</div>
                <div className="text-xl font-bold text-foreground">
                  ${vatReport.net_sales_exclusive?.toFixed(2)}
                </div>
              </div>
              <div className="bg-muted/30 rounded-xl p-4">
                <div className="text-sm text-muted-foreground">VAT Collected</div>
                <div className="text-xl font-bold text-amber-600 dark:text-amber-400">
                  ${vatReport.vat_collected?.toFixed(2)}
                </div>
              </div>
            </div>
            
            {vatReport.for_frcs_filing && (
              <div className="p-4 bg-blue-50 dark:bg-blue-950/30 rounded-xl border border-blue-200 dark:border-blue-800">
                <div className="font-medium text-blue-700 dark:text-blue-300 mb-2">FRCS Filing Summary</div>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div className="text-blue-600 dark:text-blue-400">Box 1 (Total Sales):</div>
                  <div className="text-foreground">${vatReport.for_frcs_filing.box_1_total_sales?.toFixed(2)}</div>
                  <div className="text-blue-600 dark:text-blue-400">Box 4 (Standard Rated):</div>
                  <div className="text-foreground">${vatReport.for_frcs_filing.box_4_standard_rated?.toFixed(2)}</div>
                  <div className="text-blue-600 dark:text-blue-400">Box 5 (Output VAT):</div>
                  <div className="text-foreground font-bold">${vatReport.for_frcs_filing.box_5_output_vat?.toFixed(2)}</div>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-8 text-muted-foreground">
            <Calculator className="w-12 h-12 mx-auto mb-2 opacity-30" />
            <p>Click "Generate VAT Report" to view VAT details</p>
          </div>
        )}
      </div>
    </div>
  );
}
